import StaffOriginalSection from '../features/staffNotes/StaffOriginalSection';
import {getSourceResultEntryDate as getEntryDateValue} from '../features/careSourceProcessing/sourceResultEntries';
import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CaptureSourceSummaryCard from '../features/careSourceProcessing/CaptureSourceSummaryCard';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import colors from '../assets/theme/colors';
import { useChildContext } from '../contexts/ChildContext';
import { useRole } from '../contexts/RoleContext';
import {
  getCareSourceOriginalText,
  getCareSourceTitle,
} from '../features/careSourceProcessing/careSourceStatusModel';
import {useSourceResults} from '../features/careSourceProcessing/useSourceResults';
import { isEmailSourceEntry } from '../components/Timeline/utils/sourceCaptureReceipt';
import useCareSourceReorganization from '../features/careSourceProcessing/useCareSourceReorganization';

const formatDate = (value) => {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return 'Date not set';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getEntryTitle = (entry = {}) => (
  entry.displayTitle
  || entry.title
  || entry.category
  || 'Saved timeline moment'
);

const getEntrySummary = (entry = {}) => (
  entry.notes
  || entry.body
  || entry.text
  || entry.summary
  || entry.rawText
  || ''
);

const hasCareDetails = (source = {}) => (
  Number(source.careKnowledgeSuggestionSummary?.itemCount || 0) > 0
  || Number(source.profileSuggestionSummary?.suggestionCount || 0) > 0
  || Number(source.metadata?.careSourceProcessing?.careKnowledgeItemCount || 0) > 0
);

export {normalizeSourceResultEntries} from '../features/careSourceProcessing/sourceResultEntries';

export const resolveSourceBackNavigation = (locationState = {}) => {
  const returnTo = String(locationState?.returnTo || '');
  if (returnTo === '/sources') return {to:returnTo,label:'Back to captures'};
  if (returnTo.startsWith('/messages?') || returnTo === '/messages') {
    return { to: returnTo, label: locationState?.returnLabel || 'Back to chat' };
  }
  return { to: null, label: 'Back' };
};

const SourceResultsPage = () => {
  const { sourceId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { setCurrentChildId } = useChildContext();
  const { childrenWithAccess = [], canManageChild, getUserRoleForChild } = useRole();
  const {source,entries,loading,error}=useSourceResults(sourceId,setCurrentChildId,getUserRoleForChild);
  const momentId = useMemo(() => new URLSearchParams(location.search).get('momentId') || '', [location.search]);
  const backNavigation = resolveSourceBackNavigation(location.state);

  useEffect(() => {
    if (!momentId || !entries.length) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const target = document.getElementById(`source-moment-${momentId}`);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }, 50);

    return () => window.clearTimeout(timer);
  }, [entries.length, momentId]);

  const selectedChild = useMemo(() => (
    childrenWithAccess.find((child) => child.id === source?.childId) || null
  ), [childrenWithAccess, source?.childId]);
  const sourceTitle = source ? getCareSourceTitle(source) : 'Saved source';
  const originalText = source ? getCareSourceOriginalText(source) : '';
  const careDetailsAvailable = hasCareDetails(source || {});
  const canReorganizeEmail = Boolean(
    source?.id
    && source?.childId
    && isEmailSourceEntry(source)
    && canManageChild?.(source.childId)
  );
  const emailReorganization = useCareSourceReorganization({
    enabled: canReorganizeEmail,
    sourceNoteId: source?.id || sourceId,
    useOriginalSource: true,
  });

  const openTimelineEntry = (entry) => {
    navigate('/dashboard', {
      state: {
        selectedChildId: source?.childId || null,
        childShellTarget: 'today',
        timelineEntryId: entry.id,
        selectedDate: getEntryDateValue(entry),
      },
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F7FAF8', py: { xs: 1.5, md: 3 } }}>
      <Container maxWidth="md">
        <Stack spacing={1.4}>
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => backNavigation.to ? navigate(backNavigation.to) : navigate(-1)}
            sx={{
              alignSelf: 'flex-start',
              textTransform: 'none',
              fontWeight: 850,
              color: colors.brand.deep,
            }}
          >
            {backNavigation.label}
          </Button>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 7 }}>
              <CircularProgress size={28} />
              <Typography sx={{ mt: 1, color: alpha(colors.semantic.ink, 0.62), fontWeight: 700 }}>
                Loading what was saved...
              </Typography>
            </Stack>
          ) : null}

          {!loading && error ? <Alert severity="warning">{error}</Alert> : null}

          {!loading && source ? (
            <>
              {source.staffOriginalVersion || source.sourceMetadata?.staffSigningEnabled ? <>
                <StaffOriginalSection source={source} />
                <Typography fontWeight={800}>Organized from this note — not covered by the signature</Typography>
              </> : null}
              <CaptureSourceSummaryCard source={source} title={sourceTitle}
                personName={selectedChild?.name} entryCount={entries.length} />

              {entries.length ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.2, sm: 1.45 },
                    borderRadius: '20px',
                    border: `1px solid ${alpha(colors.brand.tint, 0.16)}`,
                    bgcolor: '#FFFFFF',
                  }}
                >
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, letterSpacing: '0.08em', color: alpha(colors.semantic.ink, 0.62), textTransform: 'uppercase' }}>
                    Timeline moments saved
                  </Typography>
                  <Stack spacing={0.8} sx={{ mt: 1 }}>
                    {entries.map((entry) => (
                      <Button
                        key={entry.id}
                        id={`source-moment-${entry.id}`}
                        onClick={() => openTimelineEntry(entry)}
                        sx={{
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          textAlign: 'left',
                          p: 1,
                          borderRadius: '14px',
                          border: `1px solid ${alpha(colors.brand.tint, 0.12)}`,
                          color: colors.semantic.ink,
                          textTransform: 'none',
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                            {getEntryTitle(entry)}
                          </Typography>
                          <Typography sx={{ mt: 0.2, fontSize: '0.78rem', color: alpha(colors.semantic.ink, 0.58), fontWeight: 700 }}>
                            {formatDate(getEntryDateValue(entry))}
                          </Typography>
                          {getEntrySummary(entry) ? (
                            <Typography sx={{ mt: 0.45, color: alpha(colors.semantic.ink, 0.7), lineHeight: 1.35 }}>
                              {getEntrySummary(entry)}
                            </Typography>
                          ) : null}
                        </Box>
                      </Button>
                    ))}
                  </Stack>
                </Paper>
              ) : null}

              {careDetailsAvailable ? (
                <Button
                  variant="contained"
                  onClick={() => navigate('/care-knowledge', {
                    state: {
                      childId: source.childId || '',
                      sourceId: source.id || '',
                    },
                  })}
                  sx={{
                    alignSelf: 'flex-start',
                    borderRadius: '999px',
                    textTransform: 'none',
                    fontWeight: 850,
                    bgcolor: colors.brand.deep,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: colors.brand.ink, boxShadow: 'none' },
                  }}
                >
                  Open care details
                </Button>
              ) : null}

              {emailReorganization.action ? (
                <Button
                  variant="outlined"
                  onClick={emailReorganization.action.onClick}
                  sx={{
                    alignSelf: 'flex-start',
                    borderRadius: '999px',
                    textTransform: 'none',
                    fontWeight: 750,
                    color: colors.brand.deep,
                    borderColor: alpha(colors.brand.deep, 0.35),
                  }}
                >
                  Organize again
                </Button>
              ) : null}

              {originalText && !source.staffOriginalVersion && !source.sourceMetadata?.staffSigningEnabled ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.2, sm: 1.45 },
                    borderRadius: '18px',
                    border: `1px solid ${alpha(colors.brand.tint, 0.14)}`,
                    bgcolor: '#FFFFFF',
                  }}
                >
                  <Typography sx={{ fontWeight: 900, color: colors.semantic.ink }}>
                    Original source
                  </Typography>
                  <Typography sx={{ mt: 0.75, color: alpha(colors.semantic.ink, 0.68), whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                    {originalText}
                  </Typography>
                </Paper>
              ) : null}
            </>
          ) : null}
          {emailReorganization.sheet}
        </Stack>
      </Container>
    </Box>
  );
};

export default SourceResultsPage;
